import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import { ApprovalMode } from '../../../../api/agents-client/agent-client.type';
import { setFloraChatApprovalMode } from '../../../../store/global-store/global-store.actions';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import {
  getApprovalModeBackground,
  getApprovalModeColor,
  getCheckVisibility,
} from './flora-approval-mode-toggle.helpers';
import { useFloraApprovalMode } from './flora-approval-mode-toggle.hooks';

interface ApprovalModeMenuOption {
  mode: ApprovalMode;
  label: string;
  description: string;
  icon: string;
}

const APPROVAL_MODE_MENU_OPTIONS: ApprovalModeMenuOption[] = [
  {
    mode: ApprovalMode.Ask,
    label: 'Ask',
    description: 'Asks where you require approval',
    icon: 'material-symbols:front-hand-outline',
  },
  {
    mode: ApprovalMode.Auto,
    label: 'Auto',
    description: 'Flora handles permission decisions',
    icon: 'material-symbols:bolt-outline',
  },
];

interface FloraApprovalModeToggleProps {
  /** The chat's thread id; omit or pass empty for a brand-new chat. */
  threadId?: string;
}

/**
 * Composer menu for choosing Flora's approval mode (Ask or Auto) for one chat.
 *
 * The trigger shows the chat's own choice when it has one, otherwise the
 * user's saved default from Customize Flora (falling back to Ask while
 * preferences are loading or unavailable). The mode comes from
 * `useFloraApprovalMode`, the same hook that sets the message payload, so the
 * label always matches the mode that is sent. Picking a mode changes only this
 * chat; on a new chat it is held as a draft until the first message creates
 * the chat.
 *
 * Renders nothing unless the organization has the `flora_auto_mode` feature
 * flag; without it Flora always runs in Ask mode.
 *
 * @param {FloraApprovalModeToggleProps} props - The chat the toggle controls
 * @returns {JSX.Element | null} The approval-mode menu, or null when Auto mode is not enabled for the organization
 */
export function FloraApprovalModeToggle({ threadId }: FloraApprovalModeToggleProps) {
  const approval = useFloraApprovalMode(threadId);

  if (!approval) {
    return null;
  }

  const { mode } = approval;
  const selected = APPROVAL_MODE_MENU_OPTIONS.find((option) => option.mode === mode) ?? APPROVAL_MODE_MENU_OPTIONS[0];

  return (
    <Menu
      shadow="md"
      radius="md"
      position="top-start"
      offset={6}
      width={236}
      styles={{
        dropdown: { padding: 4 },
        item: { padding: '5px 8px' },
        itemSection: { marginInlineEnd: 8 },
      }}
    >
      <Menu.Target>
        <ModeButton
          type="button"
          data-bf="flora-approval-mode-toggle"
          aria-label={`Approval mode: ${selected.label}`}
          $mode={mode}
        >
          <IconifyIcon icon={selected.icon} width={14} height={14} />
          {selected.label}
          <IconifyIcon icon="material-symbols:expand-more" width={14} height={14} />
        </ModeButton>
      </Menu.Target>
      <Menu.Dropdown>
        {APPROVAL_MODE_MENU_OPTIONS.map((option) => (
          <Menu.Item
            key={option.mode}
            aria-current={option.mode === mode ? 'true' : undefined}
            onClick={() => setFloraChatApprovalMode(threadId, option.mode)}
            leftSection={<IconifyIcon icon={option.icon} width={14} height={14} />}
            rightSection={
              <CheckSlot aria-hidden="true" $visible={option.mode === mode}>
                <Icon name="check" size={14} color="var(--secondary-base)" />
              </CheckSlot>
            }
          >
            <ItemText>
              <Text size="small" weight="medium" color="var(--ink-darkest)">
                {option.label}
              </Text>
              <Text size="tiny" color="var(--ink-lighter)">
                {option.description}
              </Text>
            </ItemText>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

const ModeButton = styled.button<{ $mode: ApprovalMode }>`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px 0 10px;
  border: none;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  color: ${({ $mode }) => getApprovalModeColor($mode)};
  background: ${({ $mode }) => getApprovalModeBackground($mode)};
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: var(--ink-base);
    background: var(--flora-hover-subtle);
  }
`;

const ItemText = styled.div`
  line-height: 1.25;
`;

const CheckSlot = styled.span<{ $visible: boolean }>`
  display: flex;
  visibility: ${({ $visible }) => getCheckVisibility($visible)};
`;
